import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import IndeterminateSpinner from '../loading/indeterminateSpinner';
import useTwoFactorPrompt from '../login/useTwoFactorPrompt';
import useSnackbar from '../../utils/useSnackbar';
import { getUserName, isGoogleLogin } from '../../utils/auth';
import {
  finishTwoFactorSettings,
  signInForTwoFactorSettings,
} from '../../helpers/firebaseAuthentication';
import {
  enrolTotp,
  hasTotpEnrolled,
  isTwoFactorRequiredFor,
  sendVerificationEmail,
  TWO_FACTOR_CANCELLED_ERROR_CODE,
  TWO_FACTOR_EMAIL_UNVERIFIED_ERROR_CODE,
  TWO_FACTOR_REMOVAL_NOT_ALLOWED_ERROR_CODE,
  TWO_FACTOR_UNAVAILABLE_ERROR_CODE,
  unenrolTotp,
} from '../../helpers/twoFactorAuthentication';

export const STEP_PASSWORD = 'password';
export const STEP_MANAGE = 'manage';
export const STEP_DONE = 'done';
// Firebase refuses a new verification email for about a minute after the last one
export const RESEND_COOLDOWN_SECONDS = 60;

const useStyles = makeStyles((theme) => ({
  content: {
    maxWidth: 400,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'center',
  },
  action: {
    marginTop: theme.spacing(2),
    height: theme.spacing(6),
    fontWeight: 800,
  },
}));

// A switch rather than a lookup object: Sonar (S2068) reads "...password": "..."
// pairs as hard-coded credentials
const getErrorMessageKey = (error) => {
  switch (error?.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-login-credentials':
      return 'loginForm.wrong-password';
    case 'auth/too-many-requests':
      return 'loginForm.too-many-requests';
    case TWO_FACTOR_UNAVAILABLE_ERROR_CODE:
      return 'loginForm.twoFactor.unavailable';
    case TWO_FACTOR_REMOVAL_NOT_ALLOWED_ERROR_CODE:
      return 'loginForm.twoFactor.removalNotAllowed';
    default:
      return 'common.error.unknownError';
  }
};

/**
 * Two-factor authentication section of the Security page: confirms the password,
 * then sets up or removes the authenticator app
 */
function TwoFactorSettings({ onDone }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const { showError, showSuccess } = useSnackbar();
  const {
    twoFactorStep,
    requestSignInCode,
    requestEnrolmentCode,
    renderTwoFactorStep,
  } = useTwoFactorPrompt();

  const [step, setStep] = useState(STEP_PASSWORD);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [doneText, setDoneText] = useState('');
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const firebaseUser = useRef(null);
  const [enforced, setEnforced] = useState(false);

  // Leaving the page half way must not keep the user signed in to Firebase
  useEffect(
    () => () => {
      const user = firebaseUser.current;
      firebaseUser.current = null;
      if (user) {
        finishTwoFactorSettings(user, false);
      }
    },
    [],
  );

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleError = (error) => {
    setBusy(false);
    if (error?.code !== TWO_FACTOR_CANCELLED_ERROR_CODE) {
      showError(t(getErrorMessageKey(error)));
    }
  };

  const confirmPassword = async () => {
    setBusy(true);
    try {
      const user = await signInForTwoFactorSettings(
        getUserName(),
        password,
        requestSignInCode,
      );
      firebaseUser.current = user;
      setEnrolled(hasTotpEnrolled(user));
      setEnforced(await isTwoFactorRequiredFor(user));
      setStep(STEP_MANAGE);
      setBusy(false);
    } catch (error) {
      handleError(error);
    }
  };

  const finish = async (renewSession, text) => {
    const user = firebaseUser.current;
    firebaseUser.current = null;
    await finishTwoFactorSettings(user, renewSession);
    setDoneText(text);
    setStep(STEP_DONE);
    setBusy(false);
  };

  const setUp = async () => {
    setBusy(true);
    try {
      const enrolledNow = await enrolTotp(
        firebaseUser.current,
        requestEnrolmentCode,
        false,
      );
      if (enrolledNow) {
        await finish(true, t('loginForm.twoFactor.settings.enabled'));
      } else {
        setBusy(false);
      }
    } catch (error) {
      if (error?.code !== TWO_FACTOR_EMAIL_UNVERIFIED_ERROR_CODE) {
        handleError(error);
        return;
      }
      // Send the link right away the first time; later attempts offer a resend
      if (!emailUnverified) {
        await sendVerificationEmail(firebaseUser.current);
        setEmailUnverified(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
      setBusy(false);
    }
  };

  const resendVerificationEmail = async () => {
    setBusy(true);
    const sent = await sendVerificationEmail(firebaseUser.current);
    setBusy(false);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (sent) {
      showSuccess(t('loginForm.twoFactor.settings.verificationEmailSent'));
    } else {
      showError(t('loginForm.twoFactor.settings.verificationEmailFailed'));
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await unenrolTotp(firebaseUser.current);
      await finish(false, t('loginForm.twoFactor.settings.removed'));
    } catch (error) {
      handleError(error);
    }
  };

  const renderPasswordStep = () => (
    <form
      className={classes.content}
      onSubmit={(e) => {
        e.preventDefault();
        confirmPassword();
      }}
    >
      <Typography variant="body2">
        {t('loginForm.twoFactor.settings.confirmPassword')}
      </Typography>
      <TextField
        margin="normal"
        fullWidth
        autoFocus
        type="password"
        variant="outlined"
        label={t('loginForm.passwordFieldLabel')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        inputProps={{ 'data-testid': 'two-factor-settings-password' }}
      />
      <Button
        className={classes.action}
        type="submit"
        fullWidth
        color="primary"
        variant="contained"
        disabled={busy || password === ''}
        data-testid="two-factor-settings-continue"
      >
        {t('loginForm.twoFactor.settings.continue')}
      </Button>
    </form>
  );

  const renderRemoveAction = () =>
    enforced ? (
      <Box marginTop={2}>
        <Typography variant="caption" data-testid="two-factor-removal-blocked">
          {t('loginForm.twoFactor.removalNotAllowed')}
        </Typography>
      </Box>
    ) : (
      <Button
        className={classes.action}
        fullWidth
        variant="outlined"
        onClick={remove}
        disabled={busy}
        data-testid="two-factor-remove"
      >
        {t('loginForm.twoFactor.settings.remove')}
      </Button>
    );

  const renderEmailVerification = () => (
    <>
      <Box marginTop={2}>
        <Typography variant="body2" data-testid="two-factor-email-unverified">
          {t('loginForm.twoFactor.settings.verifyEmailFirst', {
            email: firebaseUser.current?.email,
          })}
        </Typography>
      </Box>
      <Button
        className={classes.action}
        fullWidth
        variant="outlined"
        onClick={resendVerificationEmail}
        disabled={busy || resendCooldown > 0}
        data-testid="two-factor-resend-verification"
      >
        {resendCooldown > 0
          ? t('loginForm.twoFactor.settings.resendVerificationEmailIn', {
              seconds: resendCooldown,
            })
          : t('loginForm.twoFactor.settings.resendVerificationEmail')}
      </Button>
    </>
  );

  const renderSetUpAction = () => (
    <>
      {emailUnverified && renderEmailVerification()}
      <Button
        className={classes.action}
        fullWidth
        color="primary"
        variant="contained"
        onClick={setUp}
        disabled={busy}
        data-testid="two-factor-set-up"
      >
        {emailUnverified
          ? t('loginForm.twoFactor.settings.emailVerifiedContinue')
          : t('loginForm.twoFactor.settings.setUp')}
      </Button>
    </>
  );

  const renderManageStep = () => (
    <div className={classes.content}>
      <Typography variant="body1" data-testid="two-factor-status">
        {enrolled
          ? t('loginForm.twoFactor.settings.statusOn')
          : t('loginForm.twoFactor.settings.statusOff')}
      </Typography>
      {enrolled ? renderRemoveAction() : renderSetUpAction()}
    </div>
  );

  const renderDoneStep = () => (
    <div className={classes.content}>
      <Typography variant="body1" data-testid="two-factor-done">
        {doneText}
      </Typography>
      <Button
        className={classes.action}
        fullWidth
        color="primary"
        variant="contained"
        onClick={onDone}
        data-testid="two-factor-back"
      >
        {t('loginForm.twoFactor.settings.backToDashboard')}
      </Button>
    </div>
  );

  const renderContent = () => {
    // Google accounts are protected by Google's own 2-step verification
    if (isGoogleLogin()) {
      return (
        <div className={classes.content}>
          <Typography variant="body1" data-testid="two-factor-google">
            {t('loginForm.twoFactor.settings.managedByGoogle')}
          </Typography>
        </div>
      );
    }
    if (twoFactorStep) {
      return renderTwoFactorStep({
        skipText: t('loginForm.twoFactor.cancel'),
        wide: true,
      });
    }
    if (busy) {
      return (
        <div className={classes.content}>
          <IndeterminateSpinner size={32} />
        </div>
      );
    }
    if (step === STEP_MANAGE) {
      return renderManageStep();
    }
    if (step === STEP_DONE) {
      return renderDoneStep();
    }
    return renderPasswordStep();
  };

  return renderContent();
}

TwoFactorSettings.propTypes = {
  onDone: PropTypes.func.isRequired,
};

export default TwoFactorSettings;
